import { useState } from 'react';
import { useForm } from '@moda/portal-stanchions';
import { CONFIG } from '../../config';
import { tc } from '../../lib/trackingContext';

type ModaNetworkFormFields = {
  name: string;
  email: string;
  business: string;
  phone: string;
  link: string;
  location: string;
  message: string;
  terms: boolean;
  subscribe: boolean;
};

const VALIDATIONS = {
  name: {
    required: {
      message: 'Please enter a name.',
      value: true
    }
  },
  email: {
    email: {
      message: 'Invalid email address.',
      value: true
    },
    required: {
      message: 'Please enter an email address.',
      value: true
    }
  },
  business: {
    required: {
      message: 'Please enter a business.',
      value: true
    }
  },
  phone: {
    phoneNumber: {
      message: 'Invalid phone number.',
      value: true
    },
    required: {
      message: 'Please enter a phone number.',
      value: true
    }
  },
  link: {
    required: {
      message: 'Please enter a website or social media link.',
      value: true
    }
  },
  location: {
    required: {
      message: 'Please enter your location.',
      value: true
    }
  }
};

const submitRequest = ({
  name,
  email,
  business,
  phone,
  link,
  location,
  message,
  subscribe
}: ModaNetworkFormFields): Promise<{ success: boolean }> =>
  fetch(`${CONFIG.MODA_API_URL}/v3.5/moda_network/network_consultation`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name,
      email,
      business,
      phone,
      link,
      location,
      message,
      subscribe
    })
  }).then(response => (response.ok ? response.json() : { success: false }));

export const useModaNetworkForm = ({ onSuccess }: { onSuccess: () => void }) => {
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (values: ModaNetworkFormFields) => {
    if (!values.terms) {
      setError(new Error('Please accept the Terms & Conditions.'));
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const { success } = await submitRequest(values);
      if (!success) throw new Error();
      if (success) {
        tc.track('Moda Network Form Submitted', {
          addToTrackingEvent: {
            name: values.name,
            email: values.email,
            business: values.business,
            phone: values.phone,
            link: values.link,
            location: values.location,
            message: values.message,
            subscribe: values.subscribe,
            terms: values.terms
          }
        });
      }
      onSuccess();
    } catch {
      setError(new Error('Unable to send your request at this time.'));
    }

    setLoading(false);
  };

  const { errors: formErrors, handleSubmit } = useForm({ validations: VALIDATIONS, onSubmit });

  return { handleSubmit, formErrors, error, loading };
};
